import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { Button } from '@/components/shared/atoms/Button'
import { useProtectedAction } from '@/hooks/useProtectedAction'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useSubscriptionStatus } from '@/hooks/api/useSubscriptions'
import { SubscriptionLevel } from '@wtf/shared'
import { useSuspenseQuery, useQueryClient } from '@tanstack/react-query'
import { collectionQueryOptions } from '@/hooks/queries/collectionQueryOptions'

// Collection Display
import LockSimpleOpenIcon from '@/icons/lock-simple-open'
import ChecklistIcon from '@/icons/checklist'
import { CollectionHeaderSection } from '@/components/collections/page/CollectionHeaderSection'
// TODO: Uncomment when looks section is re-enabled
// import { CollectionLooksLayout } from '@/components/collections/page/CollectionLooksLayout'
import { CollectionProductsLayout } from '@/components/collections/page/CollectionProductsLayout'

import { RemoveLookFromCollectionBottomSheet } from '@/components/collections/bottom-sheets/RemoveLookFromCollectionBottomSheet'
import { RemoveProductFromCollectionBottomSheet } from '@/components/collections/bottom-sheets/RemoveProductFromCollectionBottomSheet'
import { useUpdateCollection } from '@/hooks/api/useCollections'

// New Product Bottom Sheets
import { CreateProductFlow } from '@/components/create/product/CreateProductFlow'
import { useBottomNav } from '@/contexts/BottomNavContext'
import { classNames } from '@/utils/classNames'
import { useRouter } from '@tanstack/react-router'
import {
    arrayMove,
    // TODO: Uncomment when looks section is re-enabled
    // horizontalListSortingStrategy,
    SortableContext,
    rectSortingStrategy,
} from '@dnd-kit/sortable'
import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    TouchSensor,
    MouseSensor,
    closestCenter,
    useSensor,
    useSensors,
    defaultDropAnimationSideEffects,
    MeasuringStrategy,
} from '@dnd-kit/core'
import { ProductGridCard } from '@/components/products/cards/ProductGridCard/ProductGridCard'
import { ChooseFileBottomSheet } from '@/components/shared/bottom-sheets/ChooseFileBottomSheet'
import { useToast } from '@/components/shared/toast/Toast'
import { useCollectionEditStore } from '@/stores/collectionEdit'
import { useImageUpload } from '@/hooks/useImageUpload'
import { ImageUploadType } from '@wtf/shared'

export const Route = createFileRoute('/collections_/$id/edit')({
    component: CollectionEditRoute,
    loader: async ({ context: { queryClient }, params: { id } }) =>
        // Fetch the collection data
        queryClient.ensureQueryData(collectionQueryOptions(queryClient, id)),
})

function CollectionEditRoute() {
    const router = useRouter()
    const { id } = useParams({ from: '/collections_/$id/edit' })
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const { data: collection } = useSuspenseQuery(
        collectionQueryOptions(queryClient, id)
    )
    const { user } = useCurrentUser()
    // Store integration
    const {
        getEditSession,
        initializeSession,
        updateProductIds,
        updateLookIds,
        updateCoverPreview,
        clearEditSession,
        addProduct,
        removeProduct,
        removeLook,
    } = useCollectionEditStore()

    // Subscribe to store changes properly using Zustand selectors
    const session = useCollectionEditStore((state) => state.editSessions[id])
    const storeHasChanges = useCollectionEditStore((state) =>
        state.hasChanges(id)
    )

    // Initialize or restore session
    useEffect(() => {
        const existingSession = getEditSession(id)
        if (!existingSession) {
            initializeSession(id, collection)
        }
    }, [id, collection, getEditSession, initializeSession])

    // Get current session state from reactive store
    const productIds = session?.productIds || []
    const lookIds = session?.lookIds || []

    const [createNewProductFlowOpen, setCreateNewProductFlowOpen] =
        useState(false)

    // Add state for active drag items
    const [activeProductId, setActiveProductId] = useState<string | null>(null)
    const [activeLookId, setActiveLookId] = useState<string | null>(null)
    const [isDragActive, setIsDragActive] = useState(false)

    const { isBottomNavVisible } = useBottomNav()
    const { withAuth } = useProtectedAction()

    // Only track confirmation bottom sheets for deletion
    const [activeSheets, setActiveSheets] = useState({
        confirmDeleteCollectionLook: false,
        confirmDeleteCollectionProduct: false,
    })

    const [selectedProductIdToRemove, setSelectedProductIdToRemove] = useState<
        string | null
    >(null)
    const [selectedLookIdToRemove, setSelectedLookIdToRemove] = useState<
        string | null
    >(null)
    // API mutation - single update call handles all changes
    const updateCollection = useUpdateCollection()

    const { addToast } = useToast()

    // Cover upload with enhanced hook - replaces 30+ lines with 4 lines!
    const coverUpload = useImageUpload({
        type: ImageUploadType.COLLECTION_COVER,
        initialPreview: collection?.coverImageUrl || undefined,
        onSuccess: (url) => updateCoverPreview(id, url),
        onError: (error) => addToast(error, 'error'),
    })
    // Get subscription status for the current user and the collection owner
    const { data: subscriptionStatus } = useSubscriptionStatus(
        collection?.ownerId
    )

    // Determine if the current user is the owner of the collection
    const isOwner = !!user && !!collection && collection.ownerId === user.uid
    const isPaidCollection = collection?.isPaidSubscriberOnly || false
    const isPaidSubscriber =
        subscriptionStatus?.subscriptionType ===
        SubscriptionLevel.PAID_MONTHLY ||
        subscriptionStatus?.subscriptionType === SubscriptionLevel.PAID_ANNUAL

    const isLocked = isPaidCollection && !isOwner && !isPaidSubscriber

    // Redirect if not owner
    useEffect(() => {
        if (collection && !isOwner) {
            navigate({ to: `/collections/${id}`, replace: true })
        }
    }, [collection, isOwner, id, navigate])

    // Add navigation guard for unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (storeHasChanges) {
                e.preventDefault()
                e.returnValue = ''
            }
        }

        window.addEventListener('beforeunload', handleBeforeUnload)
        return () =>
            window.removeEventListener('beforeunload', handleBeforeUnload)
    }, [storeHasChanges])

    const toggleSheet = (type: string, isOpen: boolean) => {
        setActiveSheets((prev) => ({
            ...prev,
            [type]: isOpen,
        }))
    }

    const handleCloseEditMode = () => {
        if (storeHasChanges) {
            // TODO: Show confirmation dialog
            const shouldDiscard = window.confirm(
                'You have unsaved changes. Are you sure you want to discard them?'
            )
            if (shouldDiscard) {
                clearEditSession(id)
            } else {
                return // Don't navigate if user cancels
            }
        }
        navigate({ to: `/collections/${id}`, replace: true })
    }

    const openRemoveProductSheet = (productId: string) => {
        setSelectedProductIdToRemove(productId)
        toggleSheet('confirmDeleteCollectionProduct', true)
    }

    // TODO: Uncomment when looks section is re-enabled
    // const openRemoveLookSheet = (lookId: string) => {
    //     setSelectedLookIdToRemove(lookId)
    //     toggleSheet('confirmDeleteCollectionLook', true)
    // }

    const handleConfirmRemoveProduct = () => {
        if (selectedProductIdToRemove) {
            // Only update store - API calls happen on save
            removeProduct(id, selectedProductIdToRemove)
        }
        setSelectedProductIdToRemove(null)
        toggleSheet('confirmDeleteCollectionProduct', false)
    }

    const handleConfirmRemoveLook = () => {
        if (selectedLookIdToRemove) {
            // Only update store - API calls happen on save
            removeLook(id, selectedLookIdToRemove)
        }
        setSelectedLookIdToRemove(null)
        toggleSheet('confirmDeleteCollectionLook', false)
    }

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 300,
                tolerance: 8,
            },
        })
    )

    const handleDragStart = (event: DragStartEvent) => {
        const { active } = event

        setIsDragActive(true)

        if (productIds.includes(active.id as string)) {
            setActiveProductId(active.id as string)
        } else if (lookIds.includes(active.id as string)) {
            setActiveLookId(active.id as string)
        }
    }

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event

        // Reset active items and drag state
        setActiveProductId(null)
        setActiveLookId(null)
        setIsDragActive(false)

        if (!over || active.id === over.id) {
            return
        }

        // Handle product reordering
        if (
            productIds.includes(active.id as string) &&
            productIds.includes(over.id as string)
        ) {
            const oldIndex = productIds.findIndex((p) => p === active.id)
            const newIndex = productIds.findIndex((p) => p === over.id)

            if (oldIndex !== -1 && newIndex !== -1) {
                const newOrder = arrayMove(productIds, oldIndex, newIndex)
                updateProductIds(id, newOrder)
            }
        }

        // Handle look reordering
        if (
            lookIds.includes(active.id as string) &&
            lookIds.includes(over.id as string)
        ) {
            const oldIndex = lookIds.findIndex((p) => p === active.id)
            const newIndex = lookIds.findIndex((p) => p === over.id)

            if (oldIndex !== -1 && newIndex !== -1) {
                const newOrder = arrayMove(lookIds, oldIndex, newIndex)
                updateLookIds(id, newOrder)
            }
        }
    }

    const handleDragCancel = () => {
        setActiveProductId(null)
        setActiveLookId(null)
        setIsDragActive(false)
    }

    // Handle newly created product in edit mode without persisting until save
    const handleNewProductCreated = (newId: string) => {
        addProduct(id, newId)
        setCreateNewProductFlowOpen(false)
    }

    // Save all staged changes - backend handles the diff logic
    const handleSave = withAuth(async () => {
        if (!session) return

        try {
            // Single API call with complete final state - backend handles adds/removes/reordering
            await updateCollection.mutateAsync({
                id,
                data: {
                    productIds: session.productIds,
                    lookIds: session.lookIds,
                    ...(isOwner &&
                        session.coverPreview && {
                        coverImageUrl: session.coverPreview,
                    }),
                },
            })

            clearEditSession(id) // Clean up persisted state
            addToast('Collection saved successfully!', 'success')
            navigate({ to: `/collections/${id}`, replace: true })
        } catch (error) {
            console.error('Failed to save collection changes:', error)
            addToast('Failed to save changes. Please try again.', 'error')
        }
    })

    // // 8. Main UI (adapted from old CollectionPage, bottom sheets stubbed)
    // if (isLoading || userLoading || subscriptionLoading) return <div>Loading...</div>
    // if (error || !collection) return <div>Error loading collection.</div>

    // Optionally, render nothing or a loading state while redirecting
    if (collection && !isOwner) return null

    return (
        <>
            {/* Unsaved changes indicator */}
            {storeHasChanges && (
                <div className="fixed top-safe right-0 left-0 z-50 border-b border-yellow-200 bg-yellow-50 p-2 text-center text-sm text-yellow-800 md:top-0">
                    You have unsaved changes
                </div>
            )}

            <section
                className={classNames(
                    isOwner ? 'mb-34' : 'mb-16',
                    'mx-auto w-full',
                    isDragActive ? 'touch-none overflow-hidden' : '',
                    storeHasChanges ? 'pt-10' : ''
                )}
            >
                <CollectionHeaderSection
                    collection={{
                        ...collection,
                        // Use preview image if available, otherwise fallback to original
                        coverImageUrl:
                            coverUpload.previewUrl ||
                            collection.coverImageUrl ||
                            '',
                        // Use current productIds and lookIds from the editing session
                        productIds: productIds,
                        lookIds: lookIds,
                    }}
                    isOwner={isOwner}
                    onBack={() => router.history.back()}
                    onMore={() => { }}
                    showCloseIcon={true}
                    onClose={handleCloseEditMode}
                    onUpload={coverUpload.triggerUpload}
                />

                {/* Collection Products and Looks */}
                <div
                    className={`${isLocked ? 'z-50 blur-sm backdrop-blur-2xl' : ''} ${isDragActive ? 'select-none' : ''}`}
                >
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onDragCancel={handleDragCancel}
                        measuring={{
                            droppable: {
                                strategy: MeasuringStrategy.Always,
                            },
                        }}
                        modifiers={[]}
                    >
                        {/* <SortableContext
                            items={lookIds}
                            strategy={horizontalListSortingStrategy}
                        >
                            <CollectionLooksLayout
                                lookIds={lookIds}
                                handleAddLook={() =>
                                    router.navigate({
                                        to: '/looks/create',
                                        search: {
                                            collectionId: id,
                                            from: 'edit',
                                        },
                                    })
                                }
                                handleRemoveLook={openRemoveLookSheet}
                                showAdd={isOwner}
                                editMode={true}
                            />
                        </SortableContext> */}

                        <SortableContext
                            items={productIds}
                            strategy={rectSortingStrategy}
                        >
                            <CollectionProductsLayout
                                productIds={productIds}
                                handleAddProduct={() =>
                                    setCreateNewProductFlowOpen(true)
                                }
                                handleRemoveProduct={openRemoveProductSheet}
                                showAdd={isOwner}
                                editMode={true}
                                collectionId={id}
                            />
                        </SortableContext>

                        <DragOverlay
                            dropAnimation={{
                                duration: 300,
                                easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
                                sideEffects: defaultDropAnimationSideEffects({
                                    styles: {
                                        active: {
                                            opacity: '0.5',
                                        },
                                    },
                                }),
                            }}
                        >
                            {activeProductId ? (
                                <div className="scale-105 rotate-6 transform">
                                    <ProductGridCard
                                        productId={activeProductId}
                                        editMode={false}
                                        isDragging={true}
                                    />
                                </div>
                            ) : null}
                            {activeLookId ? (
                                <div className="scale-105 rotate-6 transform">
                                    {/* Add look card component here when available */}
                                    <div className="flex h-32 w-32 items-center justify-center rounded-lg bg-blue-200">
                                        Look
                                    </div>
                                </div>
                            ) : null}
                        </DragOverlay>
                    </DndContext>
                </div>

                {/* Unlock Collection Button */}
                {isLocked && (
                    <div
                        className={classNames(
                            'fixed inset-x-0 z-10 mx-auto w-full max-w-md px-2',
                            isBottomNavVisible
                                ? 'bottom-13'
                                : 'bottom-[0.875rem]'
                        )}
                    >
                        <Button
                            onClick={withAuth(() =>
                                toggleSheet('subscribe', true)
                            )}
                            className="flex w-full items-center justify-center gap-3 py-3.5 text-sm font-medium text-white"
                        >
                            <LockSimpleOpenIcon
                                fill="white"
                                width={20}
                                height={20}
                            />
                            Unlock collection
                        </Button>
                    </div>
                )}

                {/* Save Collection Button */}
                {isOwner && (
                    <div
                        className={classNames(
                            'fixed inset-x-0 z-10 mx-auto w-full max-w-md px-2',
                            isBottomNavVisible ? 'bottom-16' : 'bottom-8'
                        )}
                    >
                        <Button
                            onClick={handleSave}
                            disabled={
                                updateCollection.status === 'pending' ||
                                !storeHasChanges
                            }
                            className="flex w-full items-center justify-center gap-2 border-[1.5px] border-black py-3.5 text-sm font-medium shadow-[0_-15px_35px_rgba(255,255,255,1)]"
                        >
                            {updateCollection.status === 'pending' ? (
                                'Saving...'
                            ) : (
                                <>
                                    <ChecklistIcon
                                        className="text-xl"
                                        stroke="white"
                                    />
                                    {storeHasChanges
                                        ? 'Save changes'
                                        : 'No changes'}
                                </>
                            )}
                        </Button>
                    </div>
                )}
            </section>

            <section className="fixed">
                {/* Edit collection bottom sheet */}
                <RemoveLookFromCollectionBottomSheet
                    open={activeSheets.confirmDeleteCollectionLook}
                    onClose={() => {
                        setSelectedLookIdToRemove(null)
                        toggleSheet('confirmDeleteCollectionLook', false)
                    }}
                    onConfirm={handleConfirmRemoveLook}
                />

                <RemoveProductFromCollectionBottomSheet
                    open={activeSheets.confirmDeleteCollectionProduct}
                    onClose={() => {
                        setSelectedProductIdToRemove(null)
                        toggleSheet('confirmDeleteCollectionProduct', false)
                    }}
                    onConfirm={handleConfirmRemoveProduct}
                />

                {createNewProductFlowOpen && (
                    <CreateProductFlow
                        /** in edit mode, hide add-to-collection sheet and close on save */
                        hideAddToCollection={true}
                        onClose={() => setCreateNewProductFlowOpen(false)}
                        onProductCreated={handleNewProductCreated}
                    />
                )}
                <ChooseFileBottomSheet {...coverUpload.filePickerProps} />
            </section>
        </>
    )
}
